// Feste Texte und Schlüssel der Kasse – für Server UND Oberfläche. Der Hinweis auf den Demomodus steht hier genau
// einmal; jeder Kassenbildschirm, jeder Bon und jeder Bericht nimmt ihn von hier.

import { brand } from '@shared/brand'

/**
 * Pflichthinweis auf jedem Bildschirm der Kasse, solange der Betrieb keine Einheit im Echtbetrieb hat
 * (ENTSCHEIDUNGEN §3.3) – wörtlich, nicht kürzen. Texte mit der Abkürzung der Sicherheitseinrichtung stehen allein in
 * fiscalLabels.ts.
 */
export { POS_BANNER, POS_FISCAL_NOTICE } from './fiscalLabels'
/** Marke auf jedem Bon und jedem Bericht außerhalb des Echtbetriebs */
export const POS_DEMO_MARK = 'DEMO'
export const POS_RECEIPT_NOTICE = 'DEMO – kein gültiger Kassenbeleg. Dieser Bon ist kein Fiskalbeleg; er stammt aus dem Schulungs- und Demomodus.'
export const POS_REPORT_NOTICE = 'DEMO – kein Tagesabschluss für die Buchführung. Schulungs- und Demomodus.'
/** Schulungsbon (Trainingsmodus der Sitzung oder Betrieb im Schulungsbetrieb) */
export const POS_TRAINING_MARK = 'TRAINING'
export const POS_TRAINING_NOTICE = 'TRAINING – Schulungsbon, kein Kassenbeleg. Ohne Bestand, ohne Zahlung, ohne Umsatz.'
export const POS_CARD_NOTICE = 'Kartenzahlung über ein externes Terminal: Betrag dort eingeben und hier von Hand bestätigen. Es gibt keine Anbindung an das Terminal.'
export const POS_OFFLINE_NOTICE = 'Keine Verbindung zum Server. Die Kasse verkauft nur mit Verbindung – bitte warten Sie, bis sie wieder steht.'

/** Kassensoftware, wie sie in Abschluss, Export und Datenblatt steht (Software-Version: bei jeder Auslieferung anheben). */
export const POS_SOFTWARE = { brand: brand.name, model: `${brand.name} Kasse (Browser)`, softwareBrand: brand.name, version: '0.1.0' } as const

export const RECEIPT_TYPE = 'pos_receipt'
export const RETURN_TYPE = 'pos_return'
export const POS_DOCUMENT_TYPES = [RECEIPT_TYPE, RETURN_TYPE] as const
/** Nummernkreis der Bargeldbewegungen (frei, je Kasse) */
export const CASH_RANGE = 'pos_cash'

export const RECEIPT_STATUS_LABELS: Record<string, string> = {
  draft: 'Offen',
  posted: 'Bezahlt',
  partially_returned: 'Teilweise zurückgenommen',
  returned: 'Zurückgenommen',
  reversed: 'Storniert',
  cancelled: 'Abgebrochen',
}
export const RETURN_STATUS_LABELS: Record<string, string> = { draft: 'Offen', posted: 'Erstattet', reversed: 'Storniert', cancelled: 'Abgebrochen' }

/** Zahlarten an der Kasse → Zahlart des Kerns (payment.method) */
export const PAY_METHODS = ['cash', 'card', 'voucher'] as const
export type PayMethod = (typeof PAY_METHODS)[number]
export const PAY_METHOD_LABELS: Record<PayMethod, string> = { cash: 'Bar', card: 'Karte', voucher: 'Gutschein' }
export const KERNEL_METHOD: Record<PayMethod, 'cash' | 'ec_card' | 'voucher'> = { cash: 'cash', card: 'ec_card', voucher: 'voucher' }
export const payMethodOf = (kernelMethod: string): PayMethod | null => (kernelMethod === 'cash' ? 'cash' : kernelMethod === 'ec_card' || kernelMethod === 'credit_card' ? 'card' : kernelMethod === 'voucher' ? 'voucher' : null)
export const payMethodLabel = (kernelMethod: string): string => {
  const method = payMethodOf(kernelMethod)
  return method ? PAY_METHOD_LABELS[method] : kernelMethod === 'deposit_voucher' ? 'Pfandbon' : 'Sonstige'
}

/**
 * Bargeld außerhalb von Verkäufen – je Art ein Geschäftsvorfall:
 *   deposit Privateinlage · withdrawal Privatentnahme · transfer Geldtransit an Bank/Tresor · transfer_in Wechselgeld
 *   aus Tresor/Bank (Geldtransit) · payout Barauszahlung einer Ausgabe · payin Bareinzahlung
 */
export const CASH_KINDS = ['transfer_in', 'transfer', 'deposit', 'withdrawal', 'payout', 'payin'] as const
export type CashKind = (typeof CASH_KINDS)[number]
export const CASH_KIND_LABELS: Record<CashKind, string> = {
  transfer_in: 'Wechselgeld aus Tresor/Bank',
  transfer: 'Geldtransit an Bank/Tresor',
  deposit: 'Privateinlage',
  withdrawal: 'Privatentnahme',
  payout: 'Auszahlung (Ausgabe)',
  payin: 'Einzahlung',
}
/** Richtung: +1 Geld kommt in die Kasse, −1 Geld verlässt sie */
export const CASH_KIND_SIGN: Record<CashKind, 1 | -1> = { transfer_in: 1, transfer: -1, deposit: 1, withdrawal: -1, payout: -1, payin: 1 }
/** Geschäftsvorfall der Bewegung (Kassenbuch und Einzelaufzeichnung) */
export const CASH_KIND_CASE: Record<CashKind, 'Geldtransit' | 'Privateinlage' | 'Privatentnahme' | 'Auszahlung' | 'Einzahlung'> = {
  transfer_in: 'Geldtransit',
  transfer: 'Geldtransit',
  deposit: 'Privateinlage',
  withdrawal: 'Privatentnahme',
  payout: 'Auszahlung',
  payin: 'Einzahlung',
}
/** Arten mit Steuersatz (Ausgabe bzw. Einnahme mit Umsatzsteuer) */
export const CASH_KINDS_WITH_TAX: readonly CashKind[] = ['payout', 'payin']
export const CASH_TAX_RATES = [1900, 700, 0] as const

export const EVENT_KINDS = ['line_void', 'receipt_void', 'discount_grant', 'age_refused'] as const
export type EventKind = (typeof EVENT_KINDS)[number]
export const EVENT_KIND_LABELS: Record<EventKind, string> = {
  line_void: 'Position storniert',
  receipt_void: 'Bon abgebrochen',
  discount_grant: 'Rabatt gewährt',
  age_refused: 'Alter nicht nachgewiesen',
}

/** Stückelung des Euro-Bargelds in Cent, groß nach klein */
export const DENOMINATIONS = [20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1] as const
export const isDenomination = (cents: number): boolean => (DENOMINATIONS as readonly number[]).includes(cents)
export const denominationLabel = (cents: number): string => (cents >= 100 ? `${cents / 100} €` : `${cents} ct`)
export const isNote = (cents: number): boolean => cents >= 500

export type ShiftState = 'open' | 'closed' | 'pending_approval' | 'approved'
export const SHIFT_STATE_LABELS: Record<ShiftState, string> = {
  open: 'Offen',
  closed: 'Abgeschlossen',
  pending_approval: 'Freigabe offen',
  approved: 'Freigegeben',
}

/** Beträge eines Wertgutscheins, die die Kasse anbietet */
export const VOUCHER_AMOUNTS = [1000, 2000, 2500, 5000] as const
export const VOUCHER_MIN_CENTS = 100
export const VOUCHER_MAX_CENTS = 50_000
export const VOUCHER_LINE_FLAG = 'posVoucher'

// =====================================================================================================================
// Kasse ohne Verbindung (docs/kasse-offline/SPEZIFIKATION.md) – Texte wörtlich nach der Spezifikation; Texte mit der
// Abkürzung der Sicherheitseinrichtung stehen in fiscalLabels.ts (FISCAL_OFFLINE_*, OFFLINE_BANNER_*, OFFLINE_RETURN_TEXT).
// Platzhalter in geschweiften Klammern ({n}, {amount} …) füllt fillText(); Beträge, Zeiten und Daten formatiert der
// Aufrufer vorher über shared/format.ts. Offline ist ein Störfallprozess (§2.4) – kein Text beschreibt ihn als Lösung
// für unsichere Leitungen.
// =====================================================================================================================

/** Füllt die Platzhalter {name} eines Textes. Ein fehlender Wert ist ein Fehler im Code (kein halber Satz am Bildschirm). */
/** „1 Bon“, „2 Bons“ – Zahl der Bons in Sätzen der Kasse. */
export const bonCount = (n: number): string => (n === 1 ? '1 Bon' : `${n} Bons`)

export function fillText(template: string, values: Readonly<Record<string, string | number>> = {}): string {
  return template.replace(/\{([A-Za-z][A-Za-z0-9]*)\}/g, (_match, key: string) => {
    const value = values[key]
    if (value === undefined) throw new Error(`fillText: Für den Platzhalter {${key}} fehlt der Wert.`)
    return String(value)
  })
}

/** Notiz des technischen Bonabbruchs bei T1 (§7.2). */
export const OFFLINE_TECH_ABORT_NOTE = 'Verbindung zum Server während des Bons unterbrochen – technischer Abbruch, der Bon wird unter neuer Vorgangsnummer fortgesetzt'

/** Verbindungszeichen in der Kopfzeile der Kasse (§15.1) – online ohne Zeichen. */
export const OFFLINE_STATE_LABELS = {
  online: '',
  pruefen: 'Verbindung wird geprüft …',
  stoerung: 'Server gestört',
  offline: 'Ohne Verbindung',
  rueckkehr: 'Verbindung zurück',
  gesperrt: 'Gesperrt',
  paused: 'Pause',
  uploading: 'Überträgt …',
} as const

/** Leiste und Hinweise der Kasse je Zustand (§10.1, §10.2, §15.1). */
export const OFFLINE_TILL_TEXTS = {
  /** online, Sätze warten: {k} übertragen, {n} insgesamt */
  uploadProgress: '{k} von {n} Bons ohne Verbindung übertragen.',
  /** Fortschritt kurz (§10.2 Nr. 5) */
  uploadProgressShort: '{k} von {n} Bons übertragen',
  pruefenOpenBon: 'Der Bon bleibt offen, bis die Verbindung geklärt ist.',
  stoerung: 'Der Server antwortet, verarbeitet den Bon aber gerade nicht. Der Bon bleibt offen und wird erneut gesendet.',
  /** stoerung länger als 3 min (§10.1) */
  stoerungBlocked: 'Der Server antwortet, verarbeitet die Kasse aber nicht. Bitte nutzen Sie den Papier-Notbetrieb.',
  /** Zähler der Leiste: {bons} = bonCount(n), seit {since} (hh:mm), noch {rest} (h Std. m Min.) */
  offlineCounter: '{bons} ohne Verbindung · seit {since} · noch {rest} möglich',
  /** Zähler am Telefon eingeklappt (§15.5): {bons} = bonCount(n) */
  offlineCounterShort: '{bons} · {since}',
  /** rueckkehr: geparkte Offline-Bons */
  returnParkedFinish: 'Abschließen',
  returnParkedAbort: 'Abbrechen',
  /** T4: offline begonnene Bons nach der Wiederverbindung (§7.2) */
  returnFinishRule: 'Vorgänge, die im Ausfall beginnen, laufen ohne Absicherung zu Ende',
  /** Übertragung stockt: {message} Satz des Servers */
  uploadStuck: 'Übertragung unterbrochen: {message}. Die Kasse verkauft weiter und versucht es erneut.',
  /** Klärfall entstanden: {bonId}, {title} */
  caseToast: 'Bon Nr. {bonId}: {title}. Die Filialleitung sieht ihn unter ‚Offline-Kassen‘.',
  /** gesperrt nach der Höchstdauer: {hours} */
  blockedDuration: 'Seit {hours} Stunden ohne Verbindung – ohne Verbindung wird nicht weiter kassiert.',
  paused: 'Kasse pausiert – {name} setzt fort.',
  resume: 'Fortsetzen',
  /** Offline-Kasse ohne Offline-Freigabe, einmalig online: {reason} */
  notAllowed: 'Diese Kasse verkauft ohne Verbindung nicht: {reason}.',
  /** App, einmalig online (§3.4, §15.1) */
  appColdStart: 'Ohne Verbindung neu starten ist in der App nicht möglich. Für den Betrieb ohne Verbindung richten Sie die Kasse im Browser bzw. auf dem iPad als Web-App vom Home-Bildschirm ein.',
  secondWindow: 'Diese Kasse ist auf diesem Gerät schon in einem anderen Fenster geöffnet.',
  secondWindowSwitch: 'Dorthin wechseln',
  /** T1: {n} neue Vorgangsnummer */
  continued: 'Der Bon läuft ohne Verbindung weiter (neue Vorgangsnummer {n}).',
  /** Feld bei Kartenzahlung ohne Verbindung (Pflicht) */
  cardReference: 'Belegnummer des Terminals',
  /** T2 mit Karte ohne Belegnummer bzw. Kartenzahlung ohne Belegnummer */
  cardReferenceRequired: 'Bitte geben Sie die Belegnummer des Terminals ein – ohne Verbindung ist sie Pflicht.',
  /** Versionswechsel der Datenbank (§4.4, §9.1) */
  newVersion: 'Neue Fassung – bitte Fenster neu laden',
  versionBlocked: 'Die Kasse ist in einem anderen Fenster offen. Bitte schließen Sie es.',
  /** Neu laden gesperrt (§14.7 setReloadBlocker): {reason} */
  reloadBlocked: '{reason} – Neu laden ist danach möglich.',
  reloadReasonBon: 'Ein Bon läuft',
  reloadReasonQueue: 'Bons ohne Verbindung warten auf die Übertragung',
  /** Abmelden gesperrt (§12.2) */
  logoutBlocked: 'Abmelden ist erst möglich, wenn alle Bons übertragen sind.',
  /** Schreibregel (§9.1) */
  saveFailed: 'Der Bon konnte auf diesem Gerät nicht gespeichert werden. Es wurde nichts kassiert. Bitte nutzen Sie den Papier-Notbetrieb.',
  entryTooLarge: 'Der Bon ist zu groß für den Betrieb ohne Verbindung – bitte teilen Sie ihn auf.',
  /** Uhr zurückgestellt (§9.5) */
  clockBack: 'Die Uhr dieses Geräts wurde zurückgestellt. Ohne Verbindung wird nicht weiter kassiert.',
  /** Uhr läuft vor (§9.5, nur Hinweis) */
  clockDrift: 'Die Uhr dieses Geräts geht vor. Die Kasse verkauft weiter; bitte prüfen Sie Datum und Uhrzeit des Geräts.',
  /** Stammdaten-Paket (§5.1) */
  snapshotStale: 'Die Artikeldaten auf diesem Gerät sind nicht aktuell genug.',
  /** Startprüfung gescheitert (§9.1) */
  integrityFailed: 'Die Daten der Kasse auf diesem Gerät sind nicht vollständig lesbar. Ohne Verbindung wird nicht kassiert – bitte wenden Sie sich an die Filialleitung.',
  /** Speicher (§11.1) */
  storageLow: 'Auf diesem Gerät ist kaum noch Speicher frei. Ohne Verbindung wird nicht weiter kassiert.',
  storageWarn: 'Auf diesem Gerät wird der Speicher knapp.',
  /** Zähler unsicher (§7.1) */
  bonIdUncertain: 'Die Vorgangsnummer der Kasse ist noch nicht abgeglichen. Ohne Verbindung wird erst danach kassiert.',
  /** keine offene Schicht bzw. Startpaket abgelaufen (§11.1) */
  noShift: 'Ohne offene Schicht verkauft die Kasse ohne Verbindung nicht. Bitte nutzen Sie den Papier-Notbetrieb.',
  startpackExpired: 'Die Daten für den Betrieb ohne Verbindung sind abgelaufen. Bitte nutzen Sie den Papier-Notbetrieb.',
  /** Nummern widersprüchlich (§7.1) */
  bonIdConflict: 'Vorgangsnummer der Kasse widersprüchlich',
  /** Online: vorgeschlagene Nummer veraltet (§7.1, CONFLICT mit expectedBonId) */
  bonIdStale: 'Die Vorgangsnummer der Kasse ist nicht mehr aktuell. Die Kasse gleicht sich ab.',
  /** Gerät für Offline gesperrt (§10.2 Nr. 3) */
  deviceBlocked: 'Dieses Gerät ist für den Betrieb ohne Verbindung gesperrt. Die Filialleitung sieht den Klärfall unter ‚Offline-Kassen‘.',
  /** Server kennt mehr Sätze (§10.2 Nr. 3) */
  serverAhead: 'Der Server kennt mehr Sätze als dieses Gerät – wurde das Gerät aus einer alten Sicherung zurückgesetzt?',
  /** Anmeldung abgelaufen (§10.2 Nr. 4): {bons} = bonCount(n) */
  loginToUpload: 'Bitte melden Sie sich an, damit die Kasse {bons} übertragen kann.',
  /** Warnstufe 2 (§11.2): {duration} */
  warnLevel2: 'Seit {duration} ohne Verbindung. Bitte melden Sie die Störung der Filialleitung – sie ist unverzüglich zu beheben und zu dokumentieren.',
  /** Warnstufe 3 (§11.2): {rest} */
  warnLevel3: 'Noch {rest} ohne Verbindung möglich.',
  /** Hinweis nach 4 h derselben Anmeldung (§11.2): {duration}, {name} */
  operatorLong: 'Seit {duration} kassiert {name} ohne Verbindung. Eine Übergabe ist nur nach Kassieranweisung möglich (Kasse zählen, dann Papier).',
  /** Bon über der Grenze (§11.1): {amount} */
  limitBon: 'Bons über {amount} kassiert die Kasse ohne Verbindung nicht.',
  limitEmpties: 'Leergut über {amount} je Bon nimmt die Kasse ohne Verbindung nicht an.',
  limitPayout: 'Auszahlungen über {amount} je Bon leistet die Kasse ohne Verbindung nicht.',
  limitCashIn: 'Wechselgeld über {amount} je Vorgang bucht die Kasse ohne Verbindung nicht.',
  limitCount: 'Ohne Verbindung sind höchstens {n} Vorgänge je Ausfall möglich. Bitte nutzen Sie den Papier-Notbetrieb.',
  /** Soll-Bestand ungewiss (§11.6): {reason} */
  expectedUncertain: 'Soll ungewiss: {reason}',
  /** fehlende Tageszählung (§11.6): {date} */
  dayCountMissing: 'Die Tageszählung vom {date} fehlt – bitte zählen Sie jetzt.',
  /** Kopie eines Bons ohne Verbindung (§8.2) */
  copyHeading: 'Kopie',
  /** Übergabe beim Kassensturz (§12.2): {name} */
  handoverNote: 'Übergabe an {name}',
} as const

/** Ohne Verbindung gesperrte Funktionen: Hinweis beim Antippen (§6.1) – {reason} aus OFFLINE_BLOCKED_REASONS. */
export const OFFLINE_BLOCKED = 'Ohne Verbindung zum Server nicht möglich: {reason}.'

/** Gründe je gesperrter Funktion (§6.1, Spalte „Grund“ in Sätzen für die Kassenkraft). */
export const OFFLINE_BLOCKED_REASONS = {
  payment: 'Gutscheine, Pfandbons und sonstige Zahlarten prüft nur der Server – ohne Verbindung zahlen Kunden bar oder mit Karte',
  customer: 'Kunden, Kundenpreise und Rechnungen führt nur der Server',
  supervisor: 'die Freigabe einer Aufsicht prüft nur der Server',
  voucher: 'das Guthaben eines Gutscheins kennt nur der Server',
  returnOther: 'den ursprünglichen Bon und seine Restmengen kennt nur der Server – ohne Verbindung geht nur die Rücknahme gegen einen Bon dieses Ausfalls',
  cashOther: 'Privateinlage, Privatentnahme sowie Aus- und Einzahlung bucht die Kasse nur mit Verbindung',
  shift: 'Öffnen und Abschließen der Schicht brauchen alle Daten der Kasse auf dem Server',
  operator: 'eine Übergabe läuft nach Kassieranweisung – Kasse zählen, dann Papier-Notbetrieb',
  serial: 'Artikel mit Seriennummer führt nur der Server',
  serverParked: 'auf dem Server geparkte Bons sind ohne Verbindung nicht erreichbar',
  depositVoucher: 'Pfandbons löst die Kasse nur mit Verbindung ein',
} as const
export type OfflineBlockedFunction = keyof typeof OFFLINE_BLOCKED_REASONS

/** Feste Hinweise zu gesperrten Funktionen (§6.1, §6.3). */
export const OFFLINE_FUNCTION_NOTICES = {
  depositVoucher: 'Pfandbons löst die Kasse nur mit Verbindung ein. Nehmen Sie das Leergut direkt im Bon an oder lösen Sie den Pfandbon später ein.',
  training: 'Im Schulungsmodus wird ohne Verbindung nicht kassiert.',
  /** Fortsetzung eines online begonnenen Bons (§6.3) */
  continueCustomer: 'Ein Bon mit Kunde lässt sich ohne Verbindung nicht fortsetzen.',
  continueVoucher: 'Der Gutschein entfällt: Ohne Verbindung lässt er sich weder verkaufen noch einlösen. Bitte bestätigen Sie den neuen Betrag.',
  /** {name} Artikel */
  continueSerial: '{name} entfällt: Artikel mit Seriennummer verkauft die Kasse nur mit Verbindung.',
  /** Ereignis discount_grant bei der Fortsetzung: {bonId}, {name} der Aufsicht */
  continueDiscountGrant: 'online gewährt bei Vorgang {bonId} von {name}',
} as const

/** „…“-Menü der Kasse ohne Verbindung (§15.1) – online zusätzlich paperRecord. */
export const OFFLINE_MENU = {
  overview: 'Übersicht ohne Verbindung',
  count: 'Kasse zählen (ohne Abschluss)',
  dayEnd: 'Tagesende ohne Verbindung',
  pause: 'Kasse pausieren',
  backup: 'Offline-Daten sichern',
  paperBegin: 'Papier-Notbetrieb beginnen',
  paperEnd: 'Papier-Notbetrieb beenden',
  paperForm: 'Papier-Vordruck öffnen',
  reprint: 'Bon nachdrucken',
  returnOffline: 'Rücknahme gegen Offline-Bon',
  cashIn: 'Wechselgeld vom Tresor',
  cashTransfer: 'Geldtransit an den Tresor',
  paperRecord: 'Papierblatt nacherfassen',
  emergencyConcept: 'Notfallkonzept anzeigen',
  /** Warnstufe 4 (§11.2) */
  inspection: 'Übersicht für die Nachschau',
} as const

/** Übersicht ohne Verbindung (§11.6) – Abschnitte. */
export const OFFLINE_OVERVIEW_TEXTS = {
  outage: 'Ausfall',
  /** {since}, {lage}, {reason}, {duration}, {bons} = bonCount(n), {first}, {last} */
  outageLine: 'Seit {since} · {lage} · {reason} · {duration} · {bons} · Vorgänge {first}–{last}',
  bons: 'Bons seit Beginn des Ausfalls',
  totalsByMethod: 'Summen je Zahlart',
  totalsByRate: 'Summen je Steuersatz',
  expected: 'Soll-Bestand',
  printOffer: 'Übersicht drucken – sie belegt die Bons dieses Ausfalls, falls das Gerät vor der Übertragung verloren geht.',
} as const

/**
 * Kassen-Oberfläche ohne Verbindung (AP-U, additiv): Sätze, die die Spezifikation nur sinngemäß nennt (§6.1, §6.3, §7.2,
 * §11.2, §11.5, §11.6, §12.2, §15.1). Sie-Form, keine Abkürzung der Sicherheitseinrichtung (die steht nur in
 * fiscalLabels.ts). Platzhalter füllt fillText().
 */
export const OFFLINE_UI_TEXTS = {
  /** Statuszeile der Kasse */
  statusOffline: 'ohne Verbindung',
  later: 'Später',
  /** Rabatt über der eigenen Grenze ohne Verbindung: {value}, {limit} */
  discountOverLimit: '{value} Rabatt liegen über Ihrer Grenze von {limit}. Ohne Verbindung zum Server gibt es keine Freigabe einer Aufsicht.',
  discountNoRight: 'Sie dürfen an dieser Kasse keinen Rabatt geben. Ohne Verbindung zum Server gibt es keine Freigabe einer Aufsicht.',
  voidNoRight: 'Positionen nimmt ohne Verbindung nur wieder vom Bon, wer selbst stornieren darf – die Freigabe einer Aufsicht prüft nur der Server.',
  voidBonNoRight: 'Einen Bon bricht ohne Verbindung nur ab, wer selbst stornieren darf – die Freigabe einer Aufsicht prüft nur der Server.',
  voidDone: 'Der Bon ist abgebrochen. Der Abbruch steht im Bericht der Schicht.',
  /** T3 ohne bekannten Grund (älterer Spiegel) */
  voidUnknownReason: 'Bonabbruch ohne Antwort des Servers',
  ageNoRight: 'An diesem Bon wurde das Alter bereits nicht nachgewiesen. Ohne Verbindung bestätigt die Alterskontrolle danach nur, wer selbst stornieren darf.',
  /** Fehler im Programm: der Satz entspricht nicht seinem Format (wird nicht gespeichert) */
  bodyInvalid: 'Der Bon ließ sich auf diesem Gerät nicht aufzeichnen. Es wurde nichts kassiert. Bitte nutzen Sie den Papier-Notbetrieb.',
  /** Bon ohne Verbindung: Zustand und Überschrift nach dem Kassieren; {n} Vorgangsnummer */
  receiptStatus: 'Ohne Verbindung erfasst',
  receiptTitle: 'Vorgang {n} · ohne Verbindung',
  receiptHint: 'Der Bon ist auf diesem Gerät gespeichert. Die Kasse überträgt ihn, sobald die Verbindung steht.',
  /** Rückkehr (§7.2 T4) */
  returnNoNewBon: 'Ein neuer Bon beginnt erst, wenn die Bons ohne Verbindung abgeschlossen sind.',
  parkedOffline: 'Geparkte Bons ohne Verbindung',
  /** Frist abgelaufen: {n} */
  deadlineAborted: 'Die Frist ist abgelaufen: Die Kasse hat {n} offene Bons ohne Verbindung abgebrochen.',
  deadlineAbortReason: 'Frist nach der Wiederverbindung abgelaufen',
  /** Fortsetzung eines online begonnenen Bons (§6.3) */
  continueTitle: 'Bon ohne Verbindung fortsetzen',
  continueRemoveCustomer: 'Kunde entfernen und fortsetzen',
  continueConfirm: 'Neuen Betrag bestätigen',
  continueAbort: 'Bon abbrechen',
  /** {amount} */
  continueNewTotal: 'Neuer Betrag: {amount}',
  /** Platzhalter für die Aufsicht, wenn der Name nicht auf dem Gerät vorliegt */
  supervisorUnknown: 'einer Aufsicht',
  /** T2: Kartenzahlung ohne Terminal-Belegnummer */
  cardReferenceAction: 'Bon abschließen',
  /** Übertragung */
  uploadQueued: 'Wartet auf die Übertragung: {bons} ohne Verbindung.',
  /** gesperrt (vollflächig) */
  loginAction: 'Anmelden',
  backupDone: 'Die Offline-Daten sind gesichert ({file}). Bitte bewahren Sie die Datei wie Kassendaten auf.',
  backupFailed: 'Die Offline-Daten ließen sich nicht sichern: {message}',
  /** Zählen (§11.6 Nr. 5, Nr. 6) */
  countIntro: 'Zählen Sie das Bargeld in der Kasse – Scheine und Münzen je Stückelung. Den Soll-Bestand nennt die Kasse nach der Zählung.',
  countHandover: 'Übergabe an (optional)',
  countSave: 'Zählung speichern',
  /** {counted}, {expected}, {diff} */
  countResult: 'Gezählt {counted} · Soll {expected} · Differenz {diff}',
  countSaved: 'Die Zählung ist auf diesem Gerät gespeichert.',
  countNoRight: 'Zählen darf ohne Verbindung nur, wer an dieser Kasse das Recht dazu hat.',
  dayEndIntro: 'Zum Geschäftsschluss zählen Sie die Kasse. Danach druckt die Kasse die Übersicht und pausiert.',
  dayEndPrint: 'Übersicht drucken und Kasse pausieren',
  /** Pause (§6.1): {name} */
  resumedBy: '{name} setzt die Kasse fort.',
  /** {name} – Bestätigung beim Fortsetzen */
  resumeAs: 'Ich bin {name} – fortsetzen',
  pauseHint: 'Ohne Verbindung kassiert nur die angemeldete Person. Eine Übergabe ist nur nach Kassieranweisung möglich.',
  /** Bargeld (§6.1) */
  cashNoRight: 'Bargeld bewegt ohne Verbindung nur, wer an dieser Kasse das Recht dazu hat.',
  cashAmount: 'Betrag',
  cashReason: 'Grund',
  cashSave: 'Buchen',
  /** {label}, {amount} */
  cashDone: '{label} über {amount} ohne Verbindung erfasst.',
  /** Rücknahme gegen einen Offline-Bon (§6.1) */
  returnNoRight: 'Zurücknehmen darf ohne Verbindung nur, wer an dieser Kasse das Recht dazu hat.',
  returnPick: 'Bon dieses Ausfalls wählen',
  returnNoBons: 'Auf diesem Gerät gibt es keinen Bon dieses Ausfalls, der sich zurücknehmen lässt.',
  returnLines: 'Was kommt zurück?',
  /** {n} */
  returnOpen: 'noch {n} zurücknehmbar',
  returnDone: 'bereits zurückgenommen',
  returnReason: 'Grund der Rücknahme',
  returnCardReference: 'Belegnummer der Gutschrift am Terminal',
  returnCardReferenceRequired: 'Bitte geben Sie die Belegnummer der Gutschrift am Terminal ein – ohne Verbindung ist sie Pflicht.',
  returnSubmit: 'Rücknahme erfassen',
  returnEstimate: 'Erstattung',
  /** {amount} */
  returnRefundCash: '{amount} bar auszahlen',
  returnRefundCard: '{amount} auf die Karte gutschreiben',
  /** Papier-Notbetrieb an der Kasse (§11.5) */
  paperReserveEmpty: 'Das Gerät hat keine Vordruck-Nummer mehr frei. Bitte nutzen Sie den Vorrat an Vordrucken an der Kasse.',
  /** {sheets} */
  paperFree: 'Freie Vordruck-Nummern dieses Geräts: {sheets}',
  paperReason: 'Grund des Papier-Notbetriebs',
  paperBeginAction: 'Drucken und Papier-Notbetrieb beginnen',
  paperPrintOnly: 'Nur drucken',
  /** {sheets} */
  paperRunning: 'Papier-Notbetrieb läuft – Blatt {sheets}.',
  paperBeginDone: 'Papier-Notbetrieb begonnen – Blatt {sheets}.',
  paperEndDone: 'Papier-Notbetrieb beendet – Blatt {sheets}.',
  paperRecordOnline: 'Ein Papierblatt erfassen Sie mit Verbindung an dieser Kasse.',
  paperLineAdd: 'Zeile hinzufügen',
  paperUnused: 'Blatt war unbenutzt',
  paperSubmit: 'Blatt erfassen',
  paperIssue: 'Vordrucke ausgeben',
  paperCount: 'Anzahl Blätter',
  paperIssued: 'Ausgegebene Blätter',
  /** Übersicht ohne Verbindung (§11.6) */
  overviewEmpty: 'Seit Beginn des Ausfalls gibt es noch keinen Bon.',
  overviewNoEpisode: 'Zurzeit läuft kein Ausfall. Die Übersicht zeigt die Bons des letzten Ausfalls auf diesem Gerät.',
  overviewPrint: 'Übersicht drucken',
  overviewCopy: 'Bon als Kopie',
  overviewColumns: 'Nr. · Zeit · Betrag · Zahlart · Bediener',
  overviewKinds: 'Art',
  /** Abschluss und Rücknahme an einer Offline-Kasse (§11.3, §4.4) */
  closeOnlineOnly: 'Abschließen lässt sich die Kasse nur mit Verbindung zum Server.',
  returnOnlineOnly: 'Gegen diesen Bon nimmt die Kasse nur mit Verbindung zurück. Ohne Verbindung geht die Rücknahme gegen einen Bon dieses Ausfalls an der Kasse.',
} as const

/** Kassieranweisung ohne Verbindung (§12.2). */
export const OFFLINE_CASHIER_INSTRUCTION =
  'Ohne Verbindung kassiert nur die angemeldete Person. Bei einer Übergabe (Schichtwechsel, Pause, Aushilfe): Kasse zählen (‚Kasse zählen‘ mit Vermerk ‚Übergabe an ‹Name›‘), danach Papier-Notbetrieb für die neue Person oder Wechsel an eine andere Kasse.'

/**
 * Notfallkonzept, Kurzfassung für die Kasse (§11.6 Nr. 7). Maßgeblich ist die Verfahrensdokumentation
 * (docs/kasse/VERFAHRENSDOKUMENTATION.md Abschnitt 4.2.9, AP-D): Diese Liste ist dort wörtlich und in derselben
 * Reihenfolge abgedruckt – server/offlineDocs.test.ts vergleicht beide. Wird das Dokument geändert, wird dieser
 * Text angeglichen.
 */
export const OFFLINE_EMERGENCY_CONCEPT: readonly string[] = [
  'Keine Verbindung zum Server ist ein Störfall. Melden Sie ihn sofort der Filialleitung – er ist unverzüglich zu beheben, Ursache und Maßnahmen werden dokumentiert.',
  'Ohne Verbindung verkauft nur eine eingerichtete Offline-Kasse, und nur, solange ihr Gerät den Server technisch nicht erreicht. Jeder Bon wird auf dem Gerät gespeichert und als nicht abgesichert gekennzeichnet; nach der Wiederverbindung überträgt die Kasse die Bons von selbst.',
  'Antwortet der Server, kassiert die Kasse nicht ohne Verbindung: Der Bon bleibt offen und wird wiederholt; nach 3 Minuten gilt der Papier-Notbetrieb.',
  'Bar und mit Karte kassieren Sie weiter; bei Karte tragen Sie die Belegnummer des Terminals ein und bewahren den Terminalbeleg auf. Gutscheine, Pfandbons, Kunden auf Rechnung und Freigaben einer Aufsicht gehen nur mit Verbindung.',
  'Ohne Verbindung kassiert nur die angemeldete Person. Bei einer Übergabe zählen Sie die Kasse und wechseln danach auf Papier oder an eine andere Kasse.',
  'Ist eine Grenze erreicht, die Schicht nicht geöffnet, das Startpaket abgelaufen oder das Gerät ausgefallen, gilt der Papier-Notbetrieb mit den nummerierten Vordrucken – nie parallel zu einem Gerät, das noch kassiert. Jedes Blatt wird später an der Kasse nacherfasst.',
  'Löschen Sie nie die Website-Daten, richten Sie das Gerät nicht neu ein und melden Sie es nicht ab, solange Bons ohne Verbindung warten. Geht das Gerät verloren, meldet die Filialleitung es unter ‚Offline-Kassen‘ als verloren.',
  'Bei Geschäftsschluss ohne Verbindung: Tagesende ohne Verbindung wählen, Kasse zählen und die Übersicht drucken. Für eine Nachschau zeigen Sie die Übersicht ohne Verbindung und zählen die Kasse.',
  'Steht die Verbindung wieder, schließen Sie den laufenden Bon binnen 5 Minuten ab; danach kassiert die Kasse wieder mit Absicherung. Die Filialleitung dokumentiert Ursache und Maßnahmen und erfasst die Papierblätter nach.',
]

/** Einrichten, Abmelden, Verlust eines Geräts (§4). */
export const OFFLINE_DEVICE_TEXTS = {
  pairAction: 'Dieses Gerät als Offline-Kasse einrichten',
  /** Einrichten im gewöhnlichen Browser-Fenster (§4.2 Nr. 3) */
  pairBrowserTab: 'Sie richten die Kasse in einem Browser-Fenster ein. Daten dieses Fensters können verloren gehen – in einem privaten Fenster sicher beim Schließen. Empfohlen: als App bzw. über ‚Zum Home-Bildschirm‘ einrichten.',
  /** persist() = false (§4.2) */
  notPersisted: 'Der Browser hat den Speicher dieses Geräts nicht als dauerhaft eingestuft. Bei Platzmangel kann er Daten räumen. Installieren Sie die Kasse als App bzw. auf dem iPad über ‚Zum Home-Bildschirm‘ und richten Sie sie dort ein.',
  /** Ergebnis des Einrichtens (§4.2 Nr. 6) */
  resultStorage: 'Speicher dauerhaft: {value}',
  resultColdStart: 'Ohne Verbindung startbar: {value}',
  resultColdStartBrowser: 'ja (Browser/Web-App)',
  resultColdStartApp: 'nur warm (App)',
  resultSell: 'Ohne Verbindung verkaufen: {value}',
  yes: 'ja',
  no: 'nein',
  /** {reason} */
  noWithReason: 'nein ({reason})',
  /** Grund im Startpaket an Kassen im Echtbetrieb, solange OFFLINE_LIVE_ALLOWED = false */
  reasonLive: 'Echtbetrieb noch nicht freigegeben',
  reasonDisabled: 'Betrieb ohne Verbindung ist für diesen Betrieb nicht eingeschaltet',
  reasonDeviceBlocked: 'das Gerät ist für den Betrieb ohne Verbindung gesperrt',
  reasonNoShift: 'keine offene Schicht',
  reasonTraining: 'Schulungsbetrieb',
  /** Kasse an fremdem Gerät (§4.1 Nr. 3): {device} */
  otherDevice: 'Diese Kasse verkauft nur auf ihrem eingerichteten Gerät ({device}).',
  /** Kopplung mit gelöschtem Speicher (§4.3): {device} */
  stillPaired: 'An dieser Kasse ist noch das Gerät {device} eingerichtet. Wenn es seine Daten verloren hat, melden Sie es unter ‚Offline-Kassen‘ als verloren.',
  /** Bestätigung bei „Gerät defekt – vollständig übertragen“ (§4.3) */
  defectiveConfirm: 'Das Gerät hat nach dem letzten Online-Bon nicht ‚Ohne Verbindung‘ gezeigt.',
  /** Begründung des Abschlusses nach Verlust (§4.3): {since} letzter Kontakt */
  lostClosingReason: 'Gerät verloren – nicht übertragene Vorgänge möglich seit {since}',
  /** Aufgabe nach Verlust (§4.3): {register} */
  lostTask: 'Außerbetriebnahme der Kasse {register} binnen eines Monats mitteilen (Grund: Verlust/Diebstahl/Defekt)',
  /** Klärfall gap nach Verlust (§4.3): {from}, {to}, {max} */
  lostReserve: 'Nummern {from}–{to} für mögliche Offline-Vorgänge des verlorenen Geräts reserviert, Anzahl unbekannt, höchstens {max}',
  /** Standort ohne Markt (§4.2, F13) */
  notAStore: 'Offline-Kassen gibt es nur an Märkten – nicht in Fahrzeugen.',
  /** zu großes Stammdaten-Paket (§5.1) */
  tooManyUnits: 'Für mehr als 50 000 Gebinde an einem Standort ist der Betrieb ohne Verbindung nicht vorgesehen.',
  /** pos.register.save mit gekoppeltem Gerät (§14.2) */
  retireFirst: 'Bitte melden Sie zuerst das Gerät ab.',
  /** Abschluss gesperrt (§11.3): {n} */
  closePending: 'Die Kasse hat noch {n} Bons ohne Verbindung, die nicht übertragen sind. Der Abschluss ist erst danach möglich.',
  closeCases: 'Für diese Schicht sind noch {n} Klärfälle offen.',
} as const

/** Verwaltung „Offline-Kassen“ (§15.2) – Seiten, Spalten, Aktionen. */
export const OFFLINE_ADMIN_TEXTS = {
  devicesTitle: 'Offline-Kassen',
  casesTitle: 'Klärfälle',
  entriesTitle: 'Übertragungsprotokoll',
  importTitle: 'Offline-Daten einlesen',
  paperFormTitle: 'Papier-Vordruck',
  paperSheetsTitle: 'Papier-Notbetrieb',
  retire: 'Abmelden',
  retireDefective: 'Gerät defekt – abmelden',
  reportLost: 'Als verloren melden',
  unblock: 'Sperre aufheben',
  documentOutage: 'Ursache dokumentieren',
  columnKind: 'Art',
  columnDevice: 'Gerät',
  columnLage: 'Lage',
  columnDocumented: 'Dokumentiert',
  /** Kassenbon (ReceiptPage): {deviceTime}, {receivedAt} */
  receiptHint: 'Ohne Verbindung erfasst am {deviceTime}, übertragen am {receivedAt}',
  /** Einstellung (§11.1) */
  responsibility: 'Die Verantwortung für die ordnungsmäßige Kassenführung trägt der Betrieb; die Freigabe der Steuerkanzlei dokumentiert seine Entscheidung.',
  /** „Demo-Betrieb“ außerhalb eines Demo-Mandanten (§11.1) */
  demoApprovalRejected: 'Die Freigabe „Demo-Betrieb“ gilt nur für Demo-Mandanten. Bitte tragen Sie die Steuerkanzlei und das Datum ihrer Freigabe ein.',
  approvalMissing: 'Offline-Betrieb lässt sich nur mit Steuerkanzlei und Datum der Freigabe einschalten.',
} as const

/** Klärfälle (§10.5): Bezeichnung je Art. */
export const OFFLINE_CASE_KIND_LABELS = {
  price_hint: 'Preis oder Satz weicht ab',
  operator_hint: 'Bediener inzwischen ohne Recht',
  rights_hint: 'außerhalb der Rechte',
  clock_hint: 'Uhr auffällig',
  duplicate_online: 'Bon war schon online gebucht',
  stock_late: 'Bestand später gebucht',
  negative_stock: 'Bestand im Minus',
  outage_doc: 'Ausfall dokumentieren',
  record_mismatch: 'Aufzeichnung passt nicht',
  booking_pending: 'Buchung offen',
  tse_conflict: 'Absicherung weicht ab',
  quarantine: 'Satz nicht übernommen',
  gap: 'Nummernlücke',
  paper_pending: 'Papierblatt fehlt',
  lost_device_data: 'Daten eines abgemeldeten Geräts',
} as const

/** Klärfälle (§10.5): Erledigungen. */
export const OFFLINE_CASE_RESOLUTION_LABELS = {
  acknowledge: 'Zur Kenntnis genommen',
  retry: 'Erneut buchen',
  book_in_open_period: 'Im ersten offenen Zeitraum buchen',
  take_over: 'Nach Prüfung übernehmen',
  confirm_gap: 'Lücke bestätigen',
  confirm_unused: 'Blatt unbenutzt',
  documented: 'Dokumentiert',
} as const

/** Texte rund um Klärfälle und Übernahme (§10.4, §10.5). */
export const OFFLINE_CASE_TEXTS = {
  noteRequired: 'Bitte begründen Sie die Erledigung (mindestens 10 Zeichen).',
  /** stock_late: {stockDate}, {bonDay} */
  stockLate: 'Bestand am {stockDate} gebucht, Verkauf vom {bonDay}',
  condensed: 'verdichtet, Bestand nicht gebucht',
  checkStockTask: 'Bestand prüfen',
  /** Aufgabe an die Buchhaltung (F28): {month}, {amount}, {tax} */
  correctVatTask: 'Voranmeldung {month} berichtigen (+{amount}, {tax})',
  /** Vermerk T2 schon online gebucht (§8.4 Fall 1) */
  duplicateOnlineNote: 'Bon zusätzlich ohne Verbindung mit Ausfallkennzeichen ausgegeben',
  /** Vermerk T2 mit bestehender Absicherung (§8.4 Fall 2) */
  alreadySecuredNote: 'Bon ohne Signatur ausgegeben, Verbindung unterbrochen; Absicherung bestand bereits',
  /** Reservierung widersprüchlich (§7.4, FORBIDDEN reason range) */
  reserveRange: 'Die gemeldeten Vorgangsnummern passen nicht zu den Nummern der Kasse.',
} as const

/** Ausfall: Lage und Ursache (§8.3, §8.6). */
export const OUTAGE_LAGE_LABELS = { internet: 'Internet der Filiale', server: `${brand.name}-Server`, unknown: 'unbekannt' } as const
export const OUTAGE_CAUSE_LABELS = {
  provider: 'Internetanbieter',
  router: 'Router/WLAN der Filiale',
  power: 'Strom',
  merxo_server: `${brand.name}-Server`,
  device: 'Kassengerät',
  other: 'Sonstiges',
  unknown: 'Unbekannt',
} as const
/** Gründe, die das Gerät meldet (§8.3) und Vermerke des Servers (§8.3, §8.6). */
export const OUTAGE_REASON_TEXTS = {
  /** {message} Fehlertext des Browsers */
  network: 'Netzwerkfehler: {message}',
  /** {seconds} */
  timeout: 'Zeitüberschreitung nach {seconds} s',
  foreign: `Antwort stammt nicht von ${brand.name} (Anmeldeseite eines WLANs?)`,
  serverReported: `${brand.name}-Server meldete eine Störung`,
  beginMissing: 'Beginn nicht übertragen',
} as const

/** Export und Archiv (§16.1) – Notizen ohne die Abkürzung der Sicherheitseinrichtung. */
export const OFFLINE_EXPORT_TEXTS = {
  /** BON_NOTIZ eines Offline-Vorgangs: {device}, {received} dd.mm.yyyy hh:mm */
  note: 'Ohne Verbindung erfasst auf Kassengerät {device}, übertragen am {received}',
  noteT1: 'technischer Abbruch (Verbindung unterbrochen)',
  noteT2Secured: 'Bon ohne Signatur ausgegeben (Verbindung unterbrochen)',
  /** Papier-Nacherfassung: {sheetNo}, {date} */
  notePaper: 'Nacherfassung Blatt {sheetNo} vom {date}',
  /** Fehlerfeld bei T1 und T2 mit abgebrochener Transaktion */
  errorAborted: 'Verbindung zum Server während des Vorgangs unterbrochen – Transaktion abgebrochen',
} as const

/** Papier-Notbetrieb (§11.5). */
export const PAPER_TEXTS = {
  bonName: 'Nacherfassung Papier-Notbetrieb',
  /** BON_NOTIZ der Nacherfassung: {sheetNo}, {date}, {from}, {to}, {by} */
  bonNote: 'Nacherfassung Blatt {sheetNo} vom {date} ({from}–{to}, {by})',
  /** Finanzperiode des Blatts gesperrt (F28): {date} */
  periodLocked: 'Die Finanzperiode des {date} ist gesperrt – bitte wenden Sie sich an die Buchhaltung.',
  /** Vordruck: Kopf */
  sheetNo: 'Blatt-Nr.',
  date: 'Datum',
  oneSheetPerDay: 'Ein Blatt je Geschäftstag – über Mitternacht beginnt ein neues Blatt.',
  period: 'Beginn und Ende des Papierbetriebs',
  cashier: 'Kürzel der Kassenkraft',
  cashBegin: 'Kassenbestand bei Beginn (gezählt oder Soll laut Gerät)',
  cashEnd: 'Kassenbestand gezählt bei Ende',
  signature: 'Unterschrift',
  /** Vordruck: Spalten */
  columns: ['Nr.', 'Uhrzeit', 'Artikel', 'Menge', 'Einzelpreis', 'Pfand/Leergut', 'Steuersatz', 'Zahlart (bar/Karte + Terminal-Beleg-Nr.)', 'Betrag'],
  totals: 'Summen je Steuersatz und Zahlart',
  /** Rückseite (nach dem Muster des DFKA) */
  instructions: 'Alle Verkäufe während des Papier-Notbetriebs werden auf einem Blatt in chronologischer Reihenfolge erfasst und später an der Kasse nacherfasst. Das Blatt wird aufbewahrt.',
  unusedReason: 'Bitte begründen Sie, warum das Blatt unbenutzt ist (mindestens 10 Zeichen).',
  notIssued: 'Diese Blattnummer wurde für diese Kasse nicht ausgegeben.',
} as const

/** Mitteilungen (§8.3, §14.4, §15.4): Titel und Text; Platzhalter füllt fillText(). */
export const OFFLINE_NOTIFY_TEXTS = {
  silentTitle: 'Kasse {register} ohne Verbindung',
  silentBody: 'Die Kasse {register} ({store}) meldet sich seit {since} nicht mehr. Ist sie eine Offline-Kasse, verkauft sie ohne Verbindung weiter. Bitte prüfen Sie die Internetverbindung der Filiale – die Störung ist unverzüglich zu beheben und zu dokumentieren.',
  receivedTitle: 'Kasse {register}: {n} Bons ohne Verbindung übertragen',
  receivedBody: 'Von {from} bis {to}, Vorgänge {first}–{last}. {m} Klärfälle sind offen.',
  quarantineTitle: 'Kasse {register}: Offline-Daten nicht übernommen',
  quarantineBody: 'Ein Satz von {device} ließ sich nicht prüfen ({reason}). Das Gerät ist für den Betrieb ohne Verbindung gesperrt. Bitte prüfen Sie den Klärfall.',
  stuckTitle: 'Kasse {register}: Störung',
  /** {what} = stuckServer bzw. stuckUpload, {since}, {message} */
  stuckBody: `{what} seit {since} ({message}). Bitte wenden Sie sich an den Betreiber von ${brand.name}.`,
  stuckServer: 'Die Kasse erreicht den Server, kann aber nicht kassieren',
  stuckUpload: 'Die Übertragung der Bons ohne Verbindung stockt',
  /** Wächter (§14.4): {date} */
  staleQueue: 'Offline-Bons seit {date} nicht übertragen',
  /** Wächter, täglich (§14.4): {register} */
  notPersistedTitle: 'Kasse {register}: Speicher des Geräts nicht dauerhaft',
  notPersistedBody: 'Das Gerät der Kasse {register} läuft im Browser-Fenster bzw. ohne dauerhaften Speicher. Bitte richten Sie die Kasse als App bzw. über ‚Zum Home-Bildschirm‘ ein, damit Bons ohne Verbindung nicht verloren gehen.',
  /** Mitteilung beim Übertragen von outage_begin (§8.3 b): {register}, {from}, {to}, {n} */
  soldOffline: 'Kasse {register} hat von {from} bis {to} ohne Verbindung verkauft ({n} Bons).',
} as const

/** Audit-Einträge (Kanal pos-offline) – Klartext je Aktion. */
export const OFFLINE_AUDIT_TEXTS = {
  /** {register}, {device}, {fingerprint} 16 Zeichen */
  paired: 'Kasse {register}: Gerät {device} als Offline-Kasse eingerichtet (Schlüssel {fingerprint})',
  /** {register}, {fromSeq}, {toSeq}, {first}, {last}, {head} 16 Zeichen, {cases} */
  complete: 'Kasse {register}: Übertragung vollständig – Sätze {fromSeq}–{toSeq}, Vorgänge {first}–{last}, Kettenkopf {head}, {cases} Klärfälle',
  /** {register}, {bonId}, {amount}, {method}, {at}, {operator}, {number} */
  takenOver: 'Kasse {register}: Bon ohne Verbindung Nr. {bonId} übernommen – {amount} ({method}), erfasst {at} von {operator}, Beleg {number}',
  /** {register}, {bonId}, {txNo} – T1/T2: Beginn nur im Fiskal-Journal (erster Bonschritt zurückgerollt) */
  adoptedStart: 'Kasse {register}: Vorgang {bonId} aus der begonnenen Transaktion {txNo} der Signatureinheit angelegt (erster Bonschritt zurückgerollt) – sie wird beendet',
  /** {purpose}, {counted}, {expected}, {diff} */
  cashCount: 'Kassensturz ohne Verbindung ({purpose}): gezählt {counted}, Soll {expected}, Differenz {diff}',
  cashCountCheck: 'Kasse zählen',
  cashCountDayEnd: 'Tagesende',
} as const

/** Prüfprotokoll je Satz (§9.4, types.ts ENTRY_CHECK_KEYS): Bezeichnung je Prüfung. */
export const ENTRY_CHECK_LABELS = {
  proof: 'Geräte-Nachweis',
  device: 'Gerät',
  size: 'Größe',
  json: 'Lesbar',
  format: 'Fassung',
  schema: 'Aufbau',
  hash: 'Prüfwert',
  signature: 'Signatur des Geräts',
  binding: 'Betrieb, Kasse, Gerät',
  sequence: 'Folgenummer',
  chain: 'Kette',
  bonId: 'Vorgangsnummer',
  shift: 'Schicht',
  record: 'Aufzeichnung',
  clock: 'Uhr',
  rights: 'Rechte',
  operator: 'Bediener',
  prices: 'Preise und Sätze',
  takeover: 'Übernahme',
} as const

/** Art eines Offline-Satzes (Übertragungsprotokoll). */
export const OFFLINE_KIND_LABELS = {
  vorgang: 'Vorgang',
  outage_begin: 'Beginn des Ausfalls',
  outage_end: 'Ende des Ausfalls',
  cash_count: 'Kassensturz',
  paper_begin: 'Papier-Notbetrieb begonnen',
  paper_end: 'Papier-Notbetrieb beendet',
  note: 'Hinweis',
  unknown: 'nicht lesbar',
} as const

/** Zustand eines Geräts (Verwaltung). */
export const DEVICE_STATE_LABELS = { active: 'Eingerichtet', blocked: 'Für Offline gesperrt', retired: 'Abgemeldet', lost: 'Verloren gemeldet' } as const

/**
 * Deutsche Sätze zu den Constraints der Migration 1830 – angemeldet in modules/pos/server/index.ts über
 * registerConstraintMessages (AP-S). `fiscal_outage_open_key` (1810) gibt es seit 1830 nicht mehr; an seine Stelle tritt
 * `fiscal_outage_unit_open_key`.
 */
export const OFFLINE_CONSTRAINT_MESSAGES: Readonly<Record<string, string>> = {
  pos_offline_setting_tenant_key: 'Für diesen Betrieb gibt es die Einstellung bereits.',
  pos_offline_setting_approval_check: 'Offline-Betrieb lässt sich nur mit Steuerkanzlei und Datum der Freigabe einschalten.',
  pos_offline_setting_minutes_check: 'Die Höchstdauer ohne Verbindung liegt zwischen 30 Minuten und 24 Stunden.',
  pos_offline_setting_count_check: 'Je Ausfall sind zwischen 50 und 5 000 Vorgänge ohne Verbindung einstellbar.',
  pos_offline_setting_bon_check: 'Der Höchstbetrag je Bon liegt zwischen 10,00 € und 10 000,00 €.',
  pos_offline_setting_empties_check: 'Die Grenze für Rücknahmen liegt zwischen 0 und der Grenze je Bon.',
  pos_offline_setting_payout_check: 'Die Grenze für Auszahlungen liegt zwischen 0 und der Grenze für Rücknahmen.',
  pos_offline_setting_cash_in_check: 'Wechselgeld vom Tresor ist je Vorgang auf höchstens 2 000,00 € einstellbar.',
  pos_offline_setting_warn_check: 'Der Hinweis „bitte beheben“ kommt frühestens nach 5 Minuten und spätestens bei der Höchstdauer.',
  pos_till_device_register_key: 'An dieser Kasse ist bereits ein Gerät eingerichtet.',
  pos_till_device_fingerprint_key: 'Dieser Geräteschlüssel ist bereits eingerichtet.',
  pos_till_device_ended_check: 'Das Gerät ist abgemeldet bzw. als verloren gemeldet und lässt sich nicht mehr ändern.',
  pos_till_device_key_frozen_check: 'Schlüssel und Kasse eines Geräts sind nach dem Einrichten fest.',
  pos_till_device_seq_monotonic_check: 'Der bestätigte Stand eines Geräts kann nicht zurückgehen.',
  pos_till_device_end_check: 'Abmelden bzw. Verlust brauchen Zeitpunkt, Person, Art und Grund.',
  pos_till_device_blocked_check: 'Eine Sperre für den Betrieb ohne Verbindung braucht einen Grund.',
  pos_offline_snapshot_version_key: 'Dieses Stammdaten-Paket ist bereits abgelegt.',
  pos_offline_startpack_hash_key: 'Dieser Stand des Startpakets ist bereits abgelegt.',
  pos_offline_reservation_from_key: 'Diese Vorgangsnummern sind an der Kasse bereits reserviert.',
  pos_offline_reservation_range_check: 'Die reservierten Vorgangsnummern sind kein gültiger Bereich.',
  pos_offline_entry_seq_key: 'Dieser Satz des Geräts ist bereits übernommen.',
  pos_offline_entry_bon_key: 'Diese Vorgangsnummer ist an der Kasse bereits übernommen.',
  pos_offline_entry_quarantine_key: 'Dieser Satz liegt bereits in Quarantäne.',
  pos_offline_entry_size_check: 'Der Satz ist zu groß.',
  pos_offline_case_resolved_check: 'Ein erledigter Klärfall braucht Zeitpunkt, Person und Erledigung.',
  pos_offline_case_date_check: 'Den Buchungstag gibt es nur bei „Im ersten offenen Zeitraum buchen“ – dort ist er Pflicht.',
  pos_offline_case_note_check: 'Bitte begründen Sie die Erledigung (mindestens 10 Zeichen).',
  pos_outage_note_text_check: 'Bitte geben Sie die Ursache oder die Maßnahmen an.',
  pos_outage_note_period_check: 'Die Störung kann nicht vor ihrer Meldung behoben sein.',
  pos_paper_form_range_from_key: 'Diese Blattnummern sind an der Kasse bereits ausgegeben.',
  pos_paper_form_range_no_check: 'Eine Reihe umfasst höchstens 100 Blattnummern.',
  pos_paper_sheet_no_key: 'Dieses Blatt ist an der Kasse bereits erfasst.',
  pos_paper_sheet_lines_check: 'Ein erfasstes Blatt braucht Zeilen, Zeitraum und Kürzel; ein unbenutztes eine Begründung (mindestens 10 Zeichen).',
  pos_paper_sheet_period_check: 'Das Ende des Papierbetriebs liegt vor seinem Beginn.',
  fiscal_outage_unit_open_key: 'Für diese Signatureinheit läuft bereits ein Ausfall.',
  fiscal_outage_device_open_key: 'Für dieses Gerät läuft bereits ein Ausfall ohne Verbindung.',
  fiscal_outage_offline_check: 'Ein Ausfall ohne Verbindung braucht Gerät, Kasse, Beginn und Lage.',
  pos_vorgang_offline_check: 'Ein Vorgang ohne Verbindung braucht Gerät, Rohsatz, Folgenummer und Gerätezeit.',
  pos_vorgang_entry_key: 'Dieser Satz ist bereits als Vorgang übernommen.',
}
